import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LegalEntityDto, LegalEntityRequestDto, PaginationRequest } from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';

const PAGE_SIZE = 20;

interface UseLegalEntityInfiniteListParams {
  search?: string;
  enabled?: boolean;
}

export const useLegalEntityInfiniteList = ({ search, enabled = true }: UseLegalEntityInfiniteListParams = {}) =>
  useInfiniteQuery(
    ['legalEntitiesInfinite', search],
    async ({ pageParam = 0 }) => {
      const paginationRequest: PaginationRequest = {
        page: pageParam,
        size: PAGE_SIZE,
      };

      const requestDto: LegalEntityRequestDto = {
        paginationRequest,
      };

      if (search?.trim()) {
        requestDto.nameOrCode = search.trim();
      }

      const response = await tFinancialService.api.getAllLegalEntities(requestDto);
      return response.data;
    },
    {
      getNextPageParam: (lastPage) => {
        const data = lastPage?.data;
        if (!data || data.last) return undefined;
        return (data.number ?? 0) + 1;
      },
      enabled,
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 minutes
      refetchOnWindowFocus: false,
    }
  );

export const useCreateLegalEntity = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: LegalEntityDto) => {
      const response = await tFinancialService.api.createLegalEntity(data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['legalEntitiesInfinite']);
      },
    }
  );
};
